import Ajv8 from '@wix/dm-ajv'
import _ from 'lodash'
import type {ValidationError, ValidationResponse, ValidationService, Validator} from '@wix/document-services-types'
import {buildSchema} from './buildSchema'
import {addCustomKeywords} from './customKeywords'

const getSchemaKey = (fragmentType: string, namespace: string, dataType: string): string => {
    return `#/${fragmentType}/${namespace}/${dataType}`
}

function createService(): ValidationService {
    const ajv = new Ajv8.Ajv2019()
    addCustomKeywords(ajv)
    const validators: Record<string, Validator> = {}

    return {
        registerFragmentSchemas: (fragmentType: string, schemas: any) => {
            _.forEach(schemas, (dataTypes, namespace) => {
                _.forEach(dataTypes, (schema, dataType) => {
                    const _schemaKey = getSchemaKey(fragmentType, namespace, dataType)
                    const exists = !!ajv.getSchema(_schemaKey)
                    if (!exists) {
                        schema = buildSchema(schema)
                        ajv.addSchema(schema, _schemaKey)
                    }
                })
            })
            if (validators[fragmentType]) {
                return
            }
            validators[fragmentType] = {
                validate: (dataType: string, data: any, namespace: string): ValidationResponse => {
                    const schemaKey = getSchemaKey(fragmentType, namespace, dataType)
                    const schemaValidator = _.attempt(() => ajv.getSchema(schemaKey))
                    if (_.isError(schemaValidator)) {
                        return getInvalidResponse('schemaValidationException', [{message: schemaValidator.message}])
                    }
                    if (!schemaValidator) {
                        return getInvalidResponse('schemaValidationError', [
                            {message: `unable to retrieve the schema ${schemaKey}`}
                        ])
                    }
                    const attemptValidation = _.attempt(() => schemaValidator(data))
                    if (_.isError(attemptValidation)) {
                        return getInvalidResponse('dataValidationException', [{message: attemptValidation.message}])
                    }
                    if (attemptValidation === false) {
                        return getInvalidResponse('dataValidationError', convertAjvErrors(schemaValidator.errors))
                    }
                    return getValidResponse()
                }
            }
        },
        getFragmentValidator: (fragmentType: string) => validators[fragmentType]
    }
}

function convertAjvErrors(errors: any[] | null | undefined) {
    return errors?.map(error => _.pick(error, ['message', 'dataPath', 'keyword', 'schemaPath', 'params'])) ?? []
}

function getValidResponse(): ValidationResponse {
    return {valid: true}
}

function getInvalidResponse(errorType: string, errors: ValidationError[]): ValidationResponse {
    return {valid: false, errorType, errors}
}

const staticInstance = createService()

export {createService, staticInstance}
